import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import ScrollToTop from './ScrollToTop';
import { Suspense, lazy, useDeferredValue, useEffect } from "react";

import "react-phone-input-2/lib/style.css";
import "antd/dist/reset.css";

import { ColorProvider } from "./view/context/ColorContext";
import { ToolApparelProvider } from "./view/context/ToolApparelContext";
import { UserProvider } from "./context/UserContext";
import LayoutWrapper from "./layout/layoutWrapper/LayoutWrapper";
import View from "./view/Index";
import ViewAdmin from "./view/IndexAdmin";
import { blogUrl } from "./config/config";
import WebsiteAnnouncement from "./components/websiteAnnouncement/WebsiteAnnouncement";

const Chatbox = lazy(() => import("./components/React+Plain"));
const ProductWithCategory = lazy(() => import("./pages/ProductCategory"));
const ProductWithCategoryBrand = lazy(() => import("./pages/ProductCategoryBrand"));
const Products = lazy(() => import("./pages/Products"));
const ProductSingleCategory = lazy(() => import("./pages/ProductSingleCategory"));
const ProductSingleCategoryBrand = lazy(() => import("./pages/ProductSingleCategoryBrand"));
const CatalogSearchResult = lazy(() => import("./pages/CatalogSearchResult"));
const SingleProductDetail = lazy(() => import("./pages/SingleProductDetail"));
const CartDetail = lazy(() => import("./pages/CartDetail"));
const ProductDelivery = lazy(() => import("./pages/ProductDelivery"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const UpdateProfile = lazy(() => import("./pages/UpdateProfile"));
const CheckOut = lazy(() => import("./checkout/checkOut"));
const NewPassword = lazy(() => import("./pages/NewPassword"));
const About = lazy(() => import("./otherpages/component/about"));
const Explore = lazy(() => import("./otherpages/component/explore"));
const Passion = lazy(() => import("./otherpages/component/passion"));
const Agreement = lazy(() => import("./otherpages/component/agreement"));
const Story = lazy(() => import("./otherpages/component/story"));
const Policy = lazy(() => import("./otherpages/component/policy"));
const Solution = lazy(() => import("./otherpages/component/solution"));
const MyAccount = lazy(() => import("./pages/MyAccount"));
const Invoice = lazy(() => import("./pages/Invoice"));
const WorkOrderPage = lazy(() => import("./pages/workorderpage"));
const Orderplaced = lazy(() => import("./pages/orderplaced"));
const Home = lazy(() => import("./pages/Home"));
const Template = lazy(() => import("./pages/Template"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PreviewPage = lazy(() => import("./view/components/PreviewPage"));

const RouteFallback = () => (
  <div style={{ minHeight: "55vh", background: "#fff" }} />
);

const ExternalBlogRedirect = () => {
  useEffect(() => {
    window.location.replace(blogUrl);
  }, []);

  return <RouteFallback />;
};

const AppRoutes = () => {
  const location = useLocation();
  const deferredLocation = useDeferredValue(location);
  const isStale = location !== deferredLocation;

  return (
    <div style={{ opacity: isStale ? 0.96 : 1, transition: "opacity 120ms ease" }}>
      <Suspense fallback={<RouteFallback />}>
        <Routes location={deferredLocation}>

          <Route path="/" element={<Home />} />
          <Route path="templates" element={<Template />} />
          <Route path="templates/:categoryId" element={<Template />} />
          <Route path="templates/:categoryId/:subcategoryId" element={<Template />} />
          <Route path="products" element={<Products />} />
          <Route path="catalogsearch/result" element={<CatalogSearchResult />} />

          <Route path="products/:category/:id" element={<ProductWithCategory />} />

          <Route
            path="products/:category/:id/:subcategory/:id"
            element={<ProductSingleCategory />}
          />

          <Route
            path="products/:category/:id/:subcategory/:id/:name/:id"
            element={<SingleProductDetail />}
          />

          <Route path=":name/:id" element={<SingleProductDetail />} />
          <Route path="designeradmin" element={<ViewAdmin />} />
          <Route path="designer" element={<View />} />
          <Route path="design-preview/:email/:designName" element={<PreviewPage />} />

          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="/new-password" element={<NewPassword />} />

          <Route path="cart" element={<CartDetail />} />
          <Route path="checkout" element={<CheckOut />} />

          <Route path="blogs/*" element={<ExternalBlogRedirect />} />

          <Route path="product-delivery" element={<ProductDelivery />} />

          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="update-profile" element={<UpdateProfile />} />

          <Route path="/about" element={<About />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/passion" element={<Passion />} />
          <Route path="/agreement" element={<Agreement />} />
          <Route path="/story" element={<Story />} />
          <Route path="/policy" element={<Policy />} />
          <Route path="/solution" element={<Solution />} />

          <Route path="/accounts" element={<MyAccount />} />
          <Route path="/accounts/:tab" element={<MyAccount />} />

          <Route path="/invoice" element={<Invoice />} />
          <Route path="workorderpage" element={<WorkOrderPage />} />
          <Route path="Orderplaced" element={<Orderplaced />} />

          <Route path="/products/brand/:brandName" element={<ProductSingleCategoryBrand />} />
          <Route path="/products/brand/:brandName/:name/:id" element={<SingleProductDetail />} />
          <Route path="/products/brand" element={<ProductWithCategoryBrand />} />

          <Route path="*" element={<NotFound />} />

        </Routes>
      </Suspense>
    </div>
  );
};

function App() {
  return (
    <UserProvider>
      <BrowserRouter>
        <ScrollToTop />
        <WebsiteAnnouncement />
        <ColorProvider>
          <Suspense fallback={null}>
            <Chatbox />
          </Suspense>

          <LayoutWrapper>
            <ToolApparelProvider>
              <AppRoutes />
            </ToolApparelProvider>
          </LayoutWrapper>

        </ColorProvider>
      </BrowserRouter>
    </UserProvider>
  );
}

export default App;
